import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { Box, BoxProps, Loader, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useInView } from "motion/react";
import { RefObject, useEffect, useRef } from "react";
import { createAtom } from "shared/api/atom";
import { GqlTransport, GqlVar, gqlx } from "shared/api/gqlx";
import { StrictUndef } from "shared/api/httpClient";
import { createCmp, Sort } from "shared/utils/cmp";
import { logger } from "shared/utils/logger";
import { Exact } from "src/gql/graphql";
import { match } from "ts-pattern";
import { create, StoreApi, UseBoundStore } from "zustand";

type GqlInfiniteVars = { limit: number; offset: number | null };
type Intent<T> = { op: "ignore" } | { op: "upsert"; item: T } | { op: "delete"; id: string };

/** @public */
export type GqlPagedResult<TItem> = TItem[];

type InfiniteStoreItemState<TItem> = {
    items: Map<string, TItem>;
    orderedIds: string[];
};

type InfiniteItemStoreActions<TItem extends { id: string }> = {
    upsert: (...incoming: TItem[]) => void;
    remove: (id: string) => void;
    reset: () => void;
};

function createInitialItemState<TItem extends { id: string }>(): InfiniteStoreItemState<TItem> {
    return {
        items: new Map(),
        orderedIds: [],
    };
}

function createItemStore<TItem extends { id: string }>(sort: Sort<TItem>) {
    const cmp = createCmp(sort);

    return create<InfiniteStoreItemState<TItem> & InfiniteItemStoreActions<TItem>>((set) => ({
        ...createInitialItemState(),

        upsert(...incoming) {
            set((s) => {
                const items = new Map(s.items);
                const orderedIds = new Set(s.orderedIds);

                incoming.forEach((each) => {
                    items.set(each.id, each);
                    orderedIds.add(each.id);
                });

                return {
                    items,
                    orderedIds: [...orderedIds.values()].toSorted((a, b) => {
                        const itemA = items.get(a)!;
                        const itemB = items.get(b)!;
                        const ret = cmp(itemA, itemB);

                        return ret;
                    }),
                };
            });
        },

        remove(id) {
            set((s) => {
                const items = new Map(s.items);
                const orderedIds = s.orderedIds.filter((it) => it != id);
                items.delete(id);
                return {
                    items,
                    orderedIds,
                };
            });
        },

        reset() {
            set(createInitialItemState());
        },
    }));
}

type ItemStore<TItem extends { id: string }> = UseBoundStore<StoreApi<InfiniteStoreItemState<TItem> & InfiniteItemStoreActions<TItem>>>;

//

class GqlInfiniteItemLoader<TItem extends { id: string }, TVar extends GqlVar, TDoc> {
    protected readonly PAGE_SIZE = 10;

    constructor(
        private doc: TypedDocumentNode<TDoc, TVar & GqlInfiniteVars>,
        private derivePage: (doc: StrictUndef<TDoc>) => GqlPagedResult<TItem>,
        private sort: Sort<TItem>,
        public vars: TVar | null = null,
        private transport?: GqlTransport,
    ) {
        this.itemStore$ = createItemStore(this.sort);
    }

    itemStore$: ItemStore<TItem>;
    private hasNextPage$ = createAtom(true);
    private isLoading$ = createAtom(false);

    private cursors = new Set<number>([0]);
    private nextCursor: number = 0;

    async fetch(offset: number) {
        return await (this.transport ?? gqlx)(
            this.doc,
            Object.assign(this.vars ?? ({} as TVar), {
                limit: this.PAGE_SIZE,
                offset,
            }),
        );
    }

    useItems() {
        const orderedIds = this.itemStore$((s) => s.orderedIds);
        const items = this.itemStore$((s) => s.items);
        return orderedIds.map((id) => items.get(id)!);
    }
    useStatus() {
        const isLoading = this.isLoading$((s) => s.value);
        const hasNextPage = this.hasNextPage$((s) => s.value);
        return { isLoading, hasNextPage };
    }

    LoadTrigger = (props: Omit<BoxProps, "ref"> & { withEndIndicator?: boolean }) => {
        const { isLoading, hasNextPage } = this.useStatus();
        const [debouncedIsLoading] = useDebouncedValue(isLoading, 100);

        const ref = useRef<HTMLDivElement>(null);
        const isInView = useInView(ref as RefObject<Element>);

        useEffect(() => {
            if (isLoading || !isInView || !hasNextPage) return;
            void this.loadMore();
        }, [isInView, isLoading, hasNextPage]);

        return (
            <Box ref={ref} mih={"1px"} miw={"1px"} {...props}>
                {debouncedIsLoading && <Loader m="auto" type="dots" />}
                {!hasNextPage && props.withEndIndicator && (
                    <Text size="xs" c="dimmed" ta="center" pb="sm">
                        End of Results
                    </Text>
                )}
            </Box>
        );
    };

    async ensureData(): Promise<TItem[]> {
        if (this.itemStore$.getState().orderedIds.length == 0 && this.hasNextPage && !this.isLoading) {
            await this.loadMore();
        }

        const { orderedIds, items } = this.itemStore$.getState();
        return orderedIds.map((id) => items.get(id)!);
    }

    async ensureAll(): Promise<TItem[]> {
        if (this.hasNextPage$.getState().value) {
            await this.loadMore();
            return this.ensureAll();
        }

        return this.ensureData();
    }

    async loadMore(): Promise<void> {
        this.isLoading$.set(true);

        const next = await this.fetch(this.nextCursor);

        const items = this.derivePage(next);
        this.nextCursor += this.PAGE_SIZE;
        this.cursors.add(this.nextCursor);

        this.hasNextPage$.set(items.length >= this.PAGE_SIZE);
        this.isLoading$.set(false);

        this.itemStore$.getState().upsert(...items);
    }

    async refetch() {
        this.isLoading$.set(true);
        const nextItems: TItem[] = [];

        for (const cursor of this.cursors) {
            const page = this.derivePage(await this.fetch(cursor));

            for (const item of page) {
                nextItems.push(item);
            }
        }

        this.itemStore$.getState().reset();
        this.itemStore$.getState().upsert(...nextItems);

        this.isLoading$.set(false);
    }

    private get hasNextPage() {
        return this.hasNextPage$.getState().value;
    }

    private get isLoading() {
        return this.isLoading$.getState().value;
    }
}

//

/** @public */
export class ItemLoader<TDoc, TItem extends { id: string }> {
    constructor(
        private doc: TypedDocumentNode<TDoc, Exact<{ [key: string]: never }>>,
        private opts: {
            items: (doc: StrictUndef<TDoc>) => TItem[];
            sort?: Sort<NoInfer<TItem>>;
            transport?: GqlTransport;
        },
    ) {
        this.itemStore$ = createItemStore(this.opts.sort ?? []);
    }

    itemStore$: ItemStore<TItem>;
    private isLoading$ = createAtom(false);
    private isFetched$ = createAtom(false);

    async ensureData(): Promise<void> {
        if (this.isFetched$.getState().value) return;
        this.isLoading$.set(true);

        const items = this.opts.items(await (this.opts.transport ?? gqlx)(this.doc, {}));

        this.isLoading$.set(false);
        this.isFetched$.set(true);
        this.itemStore$.getState().upsert(...items);
    }

    load() {
        void this.ensureData();
        return this;
    }

    useItems() {
        const orderedIds = this.itemStore$((s) => s.orderedIds);
        const items = this.itemStore$((s) => s.items);
        return orderedIds.map((id) => items.get(id)!);
    }

    useStatus() {
        const isLoading = this.isLoading$((s) => s.value);
        return { isLoading };
    }
}

//

type InfiniteRepositoryLiveOpts<TItem, TSub, TVar extends GqlVar, TSubVar extends GqlVar> = {
    doc: TypedDocumentNode<TSub, TSubVar>;
    var?: TSubVar;
    intent: (update: StrictUndef<TSub>, variables: TVar, self: Map<string, TItem>) => Intent<TItem>;
};

class InfiniteRepository<TVar extends GqlVar, TDoc, TSub, TSubVar extends GqlVar, TItem extends { id: string }> {
    constructor(
        private doc: TypedDocumentNode<TDoc, TVar & GqlInfiniteVars>,
        private derivePage: (doc: StrictUndef<TDoc>) => GqlPagedResult<TItem>,
        private sort: Sort<TItem>,
        private live?: InfiniteRepositoryLiveOpts<TItem, TSub, TVar, TSubVar>,
        private transport?: GqlTransport,
    ) {
        const l = this.live;
        if (!l) return;

        (this.transport ?? gqlx).stream(l.doc, l.var ?? ({} as TSubVar)).subscribe({
            next: (value) => {
                const loaders = [...this.registry.values()];
                for (const loader of loaders) {
                    if (!loader.vars) continue;
                    const intent = l.intent(value, loader.vars, loader.itemStore$.getState().items);

                    match(intent)
                        .with({ op: "upsert" }, ({ item }) => loader.itemStore$.getState().upsert(item))
                        .with({ op: "delete" }, ({ id }) => loader.itemStore$.getState().remove(id))
                        .with({ op: "ignore" }, () => {
                            // no op
                        })
                        .exhaustive();
                }
            },
        });
    }

    private deriveKey: (variables: unknown[]) => string = (v) => JSON.stringify(v);
    private registry = new Map<string, GqlInfiniteItemLoader<TItem, TVar, TDoc>>();

    static of<TVar extends GqlVar, TDoc, TSub, TSubVar extends GqlVar, TItem extends { id: string }>(
        doc: TypedDocumentNode<TDoc, TVar>,
        opts: {
            sort?: Sort<NoInfer<TItem>>;
            live?: InfiniteRepositoryLiveOpts<NoInfer<TItem>, TSub, TVar, TSubVar>;
            page: (doc: StrictUndef<TDoc>) => GqlPagedResult<TItem>;
        },
    ) {
        return new InfiniteRepository(doc, opts.page, opts.sort ?? { by: (it) => it.id, ord: "desc" }, opts.live);
    }

    get(vars?: Omit<TVar, keyof GqlInfiniteVars>, opts?: { sort?: Sort<TItem> }) {
        const key = this.deriveKey(Object.values(vars ?? {}));
        if (!this.registry.has(key)) {
            const loader = new GqlInfiniteItemLoader(this.doc, this.derivePage, opts?.sort ?? this.sort, vars as TVar);
            this.registry.set(key, loader);
            this.loaderWasCreated(loader);
        }

        return this.registry.get(key)!;
    }

    upsert(item: TItem): void {
        for (const loader of this.registry.values()) {
            loader.itemStore$.getState().upsert(item);
        }
    }

    remove(id: string): void {
        for (const loader of this.registry.values()) {
            loader.itemStore$.getState().remove(id);
        }
    }

    //

    loaderWasCreated(_loader: GqlInfiniteItemLoader<TItem, TVar, TDoc>): void {
        //
    }

    refresh(): void {
        for (const loader of this.registry.values()) {
            void loader.refetch();
        }
    }
}

export function createInfiniteRepository<TVar extends GqlVar, TDoc, TSub, TSubVar extends GqlVar, TItem extends { id: string }>(
    doc: TypedDocumentNode<TDoc, TVar>,
    opts: {
        sort?: Sort<NoInfer<TItem>>;
        live?: InfiniteRepositoryLiveOpts<NoInfer<TItem>, TSub, TVar, TSubVar>;
        page: (doc: StrictUndef<TDoc>) => GqlPagedResult<TItem>;
        transport?: GqlTransport;
        loaderWasCreated?: (loader: GqlInfiniteItemLoader<TItem, TVar, TDoc>) => void;
        prefetchFirstPage?: boolean;
    },
) {
    return class extends InfiniteRepository<TVar, TDoc, TSub, TSubVar, TItem> {
        constructor() {
            super(doc, opts.page, opts.sort ?? [], opts.live, opts.transport);
        }

        override loaderWasCreated(loader: GqlInfiniteItemLoader<TItem, TVar, TDoc>): void {
            if (opts.prefetchFirstPage ?? true) loader.ensureData().catch(logger.log);
            opts.loaderWasCreated?.(loader);
        }
    };
}
